import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { formatDateTime } from '../../../../helpers'
import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'
import { CoursesDynamicsService } from '../../../../services/CoursesDynamics/CoursesDynamicsService'
import { CoursesDynamicsModal } from './CoursesDynamicsModal'

const CoursesDynamicsPage = () => {
  const service = new CoursesDynamicsService()
  const cancelRef = useRef(null)
  const toast = useToast()

  const [dynamics, setDynamics] = useState<ICourseInterface[]>([])
  const [filtered, setFiltered] = useState<ICourseInterface[]>([])
  const [error, setError] = useState(false)

  const [search, setSearch] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDynamic, setEditingDynamic] = useState<ICourseInterface | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idDeleting, setIdDeleting] = useState<number | null>(null)

  const fetchDynamics = async () => {
    try {
      const data = await service.list()
      setDynamics(data)
      setError(false)
    } catch (e) {
      setError(true)
    }
  }

  useEffect(() => {
    setFiltered(dynamics.filter((dynamic) => dynamic.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, dynamics])

  useEffect(() => {
    fetchDynamics()
  }, [])

  function openEditModal(dynamic: ICourseInterface) {
    setEditingDynamic(dynamic)
    setIsModalOpen(true)
  }

  function openCreateModal() {
    setEditingDynamic(null)
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    setEditingDynamic(null)
  }

  function openDeleteDialog(id: number) {
    setIdDeleting(id)
    setIsDeleteOpen(true)
  }

  function closeDeleteDialog() {
    setIsDeleteOpen(false)
    setIdDeleting(null)
  }

  async function handleDelete() {
    if (!idDeleting) return

    try {
      await service.delete(idDeleting)
      toast({
        title: 'Dinâmica excluída com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      fetchDynamics()
    } catch {
      toast({
        title: 'Erro ao excluir dinâmica',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      closeDeleteDialog()
    }
  }

  return (
    <Box>
      <Flex flex="1" justifyContent="flex-end" alignItems="center" mb={4}>
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      {error ? (
        <Center py={10}>
          <Text color="red.500">Erro ao carregar dinâmicas de ensino.</Text>
        </Center>
      ) : (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Nome</Th>
                <Th>Criado em</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center">
                    Nenhum resultado encontrado
                  </Td>
                </Tr>
              ) : (
                filtered.map((dynamic) => (
                  <Tr key={dynamic.id}>
                    <Td>{dynamic.id}</Td>
                    <Td>{dynamic.name}</Td>
                    <Td>{formatDateTime(dynamic.created_at)}</Td>
                    <Td>
                      <Tooltip label="Editar">
                        <IconButton
                          aria-label="Editar"
                          icon={<i className="lni lni-pencil" />}
                          colorScheme="blue"
                          size="sm"
                          mr={2}
                          onClick={() => openEditModal(dynamic)}
                        />
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <IconButton
                          aria-label="Excluir"
                          icon={<i className="lni lni-trash-can" />}
                          colorScheme="red"
                          size="sm"
                          onClick={() => openDeleteDialog(dynamic.id)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Tooltip label="Adicionar dinâmica de ensino" aria-label="Adicionar dinâmica de ensino" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={openCreateModal}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <CoursesDynamicsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onFinished={fetchDynamics}
        initialData={editingDynamic}
        service={service}
      />

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={closeDeleteDialog} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar exclusão
            </AlertDialogHeader>

            <AlertDialogBody>Tem certeza que deseja excluir essa dinâmica?</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteDialog}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default CoursesDynamicsPage
